import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Pencil, Send, Undo2, ExternalLink, Copy, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatRange } from "@/lib/utils_gg";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

const TABS = ["all", "draft", "pending", "changes_requested", "approved", "rejected"];

export default function OrganizerDashboard() {
  const { user, t, lang, refreshMe, checkingAuth } = useApp();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);
  const [delId, setDelId] = useState(null);
  const nav = useNavigate();
  const clone = async (id) => {
    setBusy(id);
    try { const { data } = await api.post(`/events/${id}/clone`); toast.success(t("nextEditionCreated")); nav(`/events/${data.event_id}/edit`); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(null);
  };
  const load = () => api.get("/my/events").then(r => setItems(r.data.items));
  useEffect(() => { if (user) load(); }, [user]);
  if (checkingAuth) return null;
  if (!user) return <Navigate to="/auth" />;
  const becoming = async () => { await api.post("/auth/become-organizer"); await refreshMe(); };
  const act = async (id, path, msg) => {
    setBusy(id);
    try { await api.post(`/events/${id}/${path}`); toast.success(msg); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(null);
  };
  const del = async () => {
    setBusy(delId);
    try { await api.delete(`/events/${delId}`); toast.success(t("draftDeleted")); setDelId(null); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(null);
  };
  const shown = tab === "all" ? items : items.filter(e => e.mod_status === tab || (tab === "pending" && e.mod_status === "unpublished"));
  const count = s => items.filter(e => e.mod_status === s).length;
  const tabLabel = { all: t("allTab"), draft: t("draft"), pending: t("pending"), changes_requested: t("changesRequested"), approved: t("approved"), rejected: t("rejected") };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="organizer-dashboard">
      <div className="flex items-center justify-between border-b border-foreground pb-3 flex-wrap gap-3">
        <div><div className="eyebrow text-primary">{t("dashboard")}</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter">{t("myEventsTitle")}</h1></div>
        <Link to="/submit" className="gg-btn-primary" data-testid="btn-create-event">{t("submitEvent")}</Link>
      </div>
      {user.role === "user" && (
        <div className="mt-6 p-4 border border-foreground flex items-center justify-between flex-wrap gap-3">
          <div>{t("organizerCta")}</div>
          <button onClick={becoming} className="gg-btn" data-testid="btn-become-organizer">{t("becomeOrganizer")}</button>
        </div>
      )}
      <div className="mt-6 flex gap-1 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-testid="dashboard-tabs">
        {TABS.map(s => (
          <button key={s} onClick={() => setTab(s)} data-testid={`dash-tab-${s}`} className={`gg-chip !py-2 !px-3 whitespace-nowrap ${tab === s ? "gg-chip-active" : "hover:bg-muted"}`}>
            {tabLabel[s]}{s !== "all" && count(s) > 0 && <span className="ml-1.5 opacity-80">({count(s)})</span>}
          </button>
        ))}
      </div>
      <div className="mt-4">
        {shown.length === 0 && <div className="text-center py-14 border border-dashed border-foreground eyebrow" data-testid="dashboard-empty">—</div>}
        <ul className="divide-y divide-foreground border border-foreground" data-testid="organizer-events">
          {shown.map(e => {
            const s = e.mod_status;
            const feedback = (s === "changes_requested" || s === "rejected") ? e.mod_comment : e.pending_changes_comment;
            return (
              <li key={e.event_id} className="p-4" data-testid={`org-event-${e.event_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-display uppercase text-lg leading-tight">{e.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{formatRange(e, lang)} · {e.city}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <StatusBadge status={s} />
                      {e.has_pending_changes && <span className="eyebrow !text-[10px] px-2 py-1 border border-primary text-primary" data-testid={`org-changes-pending-${e.event_id}`}>{t("changesPending")}</span>}
                      {s === "approved" && e.public_status && e.public_status !== "scheduled" && <span className="eyebrow !text-[10px] px-2 py-1 border border-foreground">{t(`public.${e.public_status}`)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/events/${e.event_id}/edit`} className="gg-btn !py-2 !px-3 text-xs" data-testid={`org-edit-${e.event_id}`}><Pencil className="w-3 h-3" />{s === "changes_requested" || s === "rejected" ? t("editAndResubmit") : t("editEvent")}</Link>
                    {s === "draft" && <button disabled={busy === e.event_id} onClick={() => act(e.event_id, "submit", t("submittedToast"))} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid={`org-submit-${e.event_id}`}><Send className="w-3 h-3" />{t("submitForReview")}</button>}
                    {(s === "changes_requested" || s === "rejected") && <button disabled={busy === e.event_id} onClick={() => act(e.event_id, "submit", t("submittedToast"))} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid={`org-resubmit-${e.event_id}`}><Send className="w-3 h-3" />{t("resubmit")}</button>}
                    {s === "pending" && <button disabled={busy === e.event_id} onClick={() => act(e.event_id, "withdraw", t("withdrawnToast"))} className="gg-btn !py-2 !px-3 text-xs" data-testid={`org-withdraw-${e.event_id}`}><Undo2 className="w-3 h-3" />{t("withdraw")}</button>}
                    {e.mod_status !== "approved" && <button disabled={busy === e.event_id} onClick={() => setDelId(e.event_id)} className="gg-btn-ghost text-xs text-muted-foreground hover:text-destructive" data-testid={`org-delete-${e.event_id}`}><Trash2 className="w-3 h-3" />{t("delete")}</button>}
                    <button disabled={busy === e.event_id} onClick={() => clone(e.event_id)} className="gg-btn !py-2 !px-3 text-xs" data-testid={`org-clone-${e.event_id}`}><Copy className="w-3 h-3" />{t("createNextEdition")}</button>
                    <Link to={`/events/${e.slug}`} className="gg-btn-ghost text-xs" data-testid={`org-open-${e.event_id}`}><ExternalLink className="w-3 h-3" />{s === "approved" ? t("openEvent") : t("preview")}</Link>
                  </div>
                </div>
                {feedback && (
                  <div className="mt-3 p-3 border border-primary text-sm" data-testid={`org-mod-comment-${e.event_id}`}>
                    <span className="eyebrow mr-2">{t("adminFeedback")}:</span> {feedback}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {delId && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setDelId(null)} data-testid="delete-event-dialog">
          <div onClick={e => e.stopPropagation()} className="gg-card bg-background p-5 sm:p-6 w-full sm:max-w-md space-y-3">
            <h3 className="font-display uppercase text-2xl">{t("deleteEventQ")}</h3>
            <p className="text-sm leading-relaxed" data-testid="delete-event-explain">{t("deleteEventExplain")}</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setDelId(null)} className="gg-btn !py-2 !px-3 text-xs" data-testid="delete-event-cancel">{t("cancel")}</button>
              <button onClick={del} disabled={busy === delId} className="gg-btn !py-2 !px-3 text-xs bg-destructive text-destructive-foreground border-destructive" data-testid="delete-event-confirm">{t("deleteEventConfirm")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
